import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { runtimeBudgetReasonMessage } from './MissionBudgetFields';
import { useAppSelector } from '@shared/store';
import { useUserMeControllerUserMeQuery } from '@shared/services/apiService';
import { createWidgetTheme, widgetColorKeys } from '@shared/theme/widgetTheme';
import { useWorkspaceScope } from './useWorkspaceScope';
import { blankWidget, normalizedOrigin, widgetConfigurationError } from './widgetValidation';
import {
  useDisableWorkspaceWidgetMutation,
  usePublishWorkspaceWidgetMutation,
  useSaveWorkspaceWidgetMutation,
  useWorkspaceWidgetQuery,
  type WidgetAppearance,
  type WidgetConfiguration,
  type WidgetRecord,
} from './widgetApi';

export const widgetEmbedCode = (id: string) => {
  const app = window.location.origin;
  const api = new URL(import.meta.env.VITE_API_URL || '/api', app).href.replace(/\/$/, '');
  const escape = (s: string) =>
    s.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;');
  return `<script src="${escape(app)}/callevate-widget.js" data-widget-id="${id}" data-api-url="${escape(api)}" defer></script>`;
};
export const WidgetAuthoring = ({
  campaignId,
  agentId,
  onDirtyChange,
}: {
  campaignId: string;
  agentId: string;
  onDirtyChange: (dirty: boolean) => void;
}) => {
  const scope = useWorkspaceScope();
  return (
    <WidgetSession
      key={`${scope}:${campaignId}:${agentId}`}
      scope={scope}
      campaignId={campaignId}
      agentId={agentId}
      onDirtyChange={onDirtyChange}
    />
  );
};
const WidgetSession = ({
  scope,
  campaignId,
  agentId,
  onDirtyChange,
}: {
  scope: string;
  campaignId: string;
  agentId: string;
  onDirtyChange: (dirty: boolean) => void;
}) => {
  const user = useAppSelector((s) => s.auth.authenticatedUser),
    me = useUserMeControllerUserMeQuery();
  const target = { scope, campaignId, agentId },
    query = useWorkspaceWidgetQuery(target, { refetchOnMountOrArgChange: true });
  const [draft, setDraft] = useState<WidgetConfiguration | null>(null),
    [baseline, setBaseline] = useState<WidgetRecord | null | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const uploadPending = useRef(false);
  const fileRead = useRef<{ reader: FileReader | null }>({ reader: null });
  const [originsText, setOriginsText] = useState<string | undefined>();
  const [notice, setNotice] = useState(''),
    [error, setError] = useState(''),
    [unknown, setUnknown] = useState(false),
    [review, setReview] = useState<'publish' | 'disable' | null>(null);
  const [save, { isLoading: saving }] = useSaveWorkspaceWidgetMutation(),
    [publish, { isLoading: publishing }] = usePublishWorkspaceWidgetMutation(),
    [disable, { isLoading: disabling }] = useDisableWorkspaceWidgetMutation();
  const alive = useRef(true),
    busy = useRef(false),
    fileEpoch = useRef(0);
  const canManage =
    !!user &&
    user.role !== 'viewer' &&
    !me.isError &&
    me.currentData?.id === user.id &&
    me.currentData.companyId === user.companyId &&
    me.currentData.role !== 'viewer' &&
    me.currentData.privileges.includes('campaign.manage');
  const stale =
    baseline !== undefined &&
    query.currentData !== undefined &&
    (baseline?.revision ?? null) !== (query.currentData.widget?.revision ?? null);
  const dirty = uploading || (!!draft && !isEqual(draft, baseline?.configuration ?? blankWidget()));
  const locked =
    !canManage || query.isError || saving || publishing || disabling || stale || unknown;
  const current = useRef({ allowed: !locked, dirty, baseline, draft });
  current.current = { allowed: !locked, dirty, baseline, draft };
  useLayoutEffect(() => {
    alive.current = true;
    const ownedFiles = fileRead.current;
    return () => {
      alive.current = false;
      ownedFiles.reader?.abort();
      ownedFiles.reader = null;
    };
  }, []);
  useLayoutEffect(() => {
    onDirtyChange(dirty);
    return () => onDirtyChange(false);
  }, [dirty, onDirtyChange]);
  useLayoutEffect(() => {
    if (baseline === undefined && query.currentData && !query.isError) {
      setBaseline(query.currentData.widget);
      setDraft(structuredClone(query.currentData.widget?.configuration ?? blankWidget()));
    }
  }, [baseline, query.currentData, query.isError]);
  const cancelUpload = () => {
    fileEpoch.current++;
    fileRead.current.reader?.abort();
    fileRead.current.reader = null;
    uploadPending.current = false;
    setUploading(false);
  };
  const accept = (record: WidgetRecord) => {
    setOriginsText(undefined);
    setBaseline(record);
    setDraft(structuredClone(record.configuration));
    setUnknown(false);
  };
  const perform = async (kind: 'save' | 'publish' | 'disable') => {
    const now = current.current;
    if (!alive.current || busy.current || uploadPending.current || !now.allowed || !now.draft)
      return;
    if (kind === 'save' && widgetConfigurationError(now.draft)) return;
    if (kind !== 'save' && (now.dirty || !now.baseline)) return;
    busy.current = true;
    setError('');
    setNotice('');
    try {
      const result =
        kind === 'save'
          ? await save({
              ...target,
              revision: now.baseline?.revision ?? null,
              configuration: structuredClone(now.draft),
            }).unwrap()
          : kind === 'publish'
            ? await publish({ id: now.baseline!.id, revision: now.baseline!.revision }).unwrap()
            : await disable({ id: now.baseline!.id, revision: now.baseline!.revision }).unwrap();
      if (alive.current) {
        accept(result);
        setReview(null);
        setNotice(
          kind === 'save'
            ? 'Widget draft saved. Publish separately to change the website.'
            : kind === 'publish'
              ? 'Widget configuration published.'
              : 'Widget disabled for new visitors.',
        );
      }
    } catch (e) {
      if (alive.current) {
        const code = (e as { status?: unknown })?.status;
        setUnknown(typeof code !== 'number' || code >= 500);
        setError(
          typeof code === 'number' && code < 500
            ? runtimeBudgetReasonMessage(e) ||
                getApiErrorMessage(
                  e,
                  'The server rejected this change. Check origins, budgets, current permissions and native runtime support; reload if the revision changed.',
                )
            : 'The outcome was not confirmed. Reload the saved configuration before another write.',
        );
      }
    } finally {
      busy.current = false;
    }
  };
  if (query.isError && !draft)
    return (
      <Alert severity="error">
        Unable to load Widget settings. <Button onClick={() => void query.refetch()}>Retry</Button>
      </Alert>
    );
  if (!draft) return <CircularProgress aria-label="Loading Widget settings" />;
  const patch = (p: Partial<WidgetConfiguration>) => {
    if (current.current.allowed) setDraft({ ...draft, ...p });
  };
  const appearance = (p: Partial<WidgetAppearance>) =>
    patch({ appearance: { ...draft.appearance, ...p } });
  const invalid = widgetConfigurationError(draft);
  return (
    <Stack gap={3}>
      <Alert severity="info">
        Widget settings save and publish independently of Studio branches. This Widget uses the
        current native agent. Save a draft, review it, then Publish to change visitor access.
        Existing sessions retain their captured version and caps.
      </Alert>
      {!canManage && (
        <Alert severity="info">
          Read-only access. Current campaign management permission is required.
        </Alert>
      )}
      {(stale || unknown) && (
        <Alert severity="warning">
          {stale
            ? 'The Widget changed on the server. Your draft is preserved.'
            : 'The last write outcome is uncertain.'}{' '}
          Copy any unsaved text before reloading saved settings.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="info">{notice}</Alert>}
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button
          variant="contained"
          disabled={locked || uploading || !dirty || !!invalid}
          onClick={() => void perform('save')}>
          Save Widget draft
        </Button>
        <Button
          disabled={locked || dirty || !baseline || !!invalid}
          onClick={() => setReview('publish')}>
          Review and publish Widget
        </Button>
        <Button
          color="error"
          disabled={locked || dirty || !baseline?.published}
          onClick={() => setReview('disable')}>
          Disable Widget
        </Button>
        <Button
          disabled={saving || publishing || disabling}
          onClick={async () => {
            if (
              dirty &&
              !window.confirm('Discard unsaved Widget settings and reload the saved configuration?')
            )
              return;
            cancelUpload();
            const generation = ++fileEpoch.current;
            try {
              const result = await query.refetch().unwrap();
              if (alive.current && generation === fileEpoch.current) {
                setOriginsText(undefined);
                setBaseline(result.widget);
                setDraft(structuredClone(result.widget?.configuration ?? blankWidget()));
                setUnknown(false);
                setError('');
              }
            } catch {
              if (alive.current) setError('Saved settings could not be reloaded.');
            }
          }}>
          Reload saved Widget
        </Button>
      </Stack>
      {baseline?.published && (
        <Typography variant="body2">
          Published {new Date(baseline.published.publishedAt).toLocaleString()} ·{' '}
          {baseline.available
            ? 'Published for allowed websites; session admission is checked at Start'
            : 'Unavailable to new sessions'}{' '}
          · Version {baseline.published.versionId}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1fr) minmax(280px,400px)' },
          gap: 3,
        }}>
        <Stack gap={2.5}>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.enabled}
                disabled={locked}
                onChange={(e) => patch({ enabled: e.target.checked })}
              />
            }
            label="Enable visitor sessions when published"
          />
          <TextField
            label="Allowed website origins"
            multiline
            value={originsText ?? draft.allowedOrigins.join('\n')}
            disabled={locked}
            helperText="One exact HTTPS origin per line, including scheme and optional port. No wildcards or URL paths. Required when enabled."
            onChange={(e) => {
              setOriginsText(e.target.value);
              patch({
                allowedOrigins: e.target.value
                  .split('\n')
                  .filter(Boolean)
                  .map((v) => normalizedOrigin(v) ?? v.trim()),
              });
            }}
          />
          <Typography variant="h6">Visitor spending caps</Typography>
          <Typography variant="body2" color="text.secondary">
            Enter approved limits explicitly. Daily credits use UTC; the first session freezes the
            ceiling for that UTC day. Changed daily caps take effect next UTC day and publishing
            never replenishes the current daily allowance. Native billing prices and consumer
            support are required.
          </Typography>
          {(
            [
              'creditsPerSession',
              'creditsPerDay',
              'maxConcurrentSessions',
              'maxSessionSeconds',
            ] as const
          ).map((key) => (
            <TextField
              key={key}
              label={
                {
                  creditsPerSession: 'Credits per session',
                  creditsPerDay: 'Credits per UTC day',
                  maxConcurrentSessions: 'Concurrent sessions (1–100)',
                  maxSessionSeconds: 'Maximum session seconds (30–3600)',
                }[key]
              }
              value={draft.budget[key] || ''}
              disabled={locked}
              type={key.startsWith('credits') ? 'text' : 'number'}
              onChange={(e) =>
                patch({
                  budget: {
                    ...draft.budget,
                    [key]: key.startsWith('credits') ? e.target.value : Number(e.target.value),
                  },
                })
              }
              slotProps={{
                htmlInput: { inputMode: key.startsWith('credits') ? 'decimal' : 'numeric' },
              }}
            />
          ))}
          {(['mode', 'size', 'placement'] as const).map((key) => (
            <TextField
              key={key}
              select
              label={{ mode: 'Widget mode', size: 'Widget size', placement: 'Placement' }[key]}
              value={draft.appearance[key]}
              disabled={locked}
              onChange={(e) => appearance({ [key]: e.target.value })}>
              {{
                mode: ['chat', 'voice'],
                size: ['full', 'compact', 'tiny'],
                placement: ['bottom-right', 'bottom-left'],
              }[key].map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </TextField>
          ))}
          <Typography variant="h6">Brand and colors</Typography>
          {widgetColorKeys.map((key) => (
            <TextField
              key={key}
              label={
                {
                  background: 'Background',
                  accent: 'Accent',
                  messageBackground: 'Message background',
                  accentText: 'Text on accent',
                  text: 'Primary text',
                  secondaryText: 'Secondary text',
                }[key]
              }
              value={draft.appearance.colors[key] ?? ''}
              disabled={locked}
              placeholder="Use theme default"
              helperText="Optional six-digit hex color; clear to use the native theme."
              onChange={(e) => {
                const colors = { ...draft.appearance.colors };
                if (e.target.value) colors[key] = e.target.value;
                else delete colors[key];
                appearance({ colors });
              }}
            />
          ))}
          <TextField
            label="Avatar HTTPS URL"
            value={
              draft.appearance.avatar?.startsWith('data:') ? '' : (draft.appearance.avatar ?? '')
            }
            disabled={locked}
            onChange={(e) => {
              cancelUpload();
              appearance({ avatar: e.target.value || null });
            }}
          />
          <Stack direction="row" gap={1}>
            <Button component="label" disabled={locked}>
              Upload avatar
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file || !current.current.allowed) return;
                  if (
                    !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
                    file.size > 262144
                  ) {
                    setError('Choose a PNG, JPEG or WebP image of at most 256 KiB.');
                    return;
                  }
                  cancelUpload();
                  const generation = ++fileEpoch.current;
                  const reader = new FileReader();
                  fileRead.current.reader = reader;
                  uploadPending.current = true;
                  setUploading(true);
                  const finish = () => {
                    if (!alive.current || generation !== fileEpoch.current) return false;
                    uploadPending.current = false;
                    setUploading(false);
                    fileRead.current.reader = null;
                    return true;
                  };
                  reader.onload = () => {
                    if (!finish()) return;
                    if (current.current.allowed && typeof reader.result === 'string')
                      setDraft((v) =>
                        v
                          ? {
                              ...v,
                              appearance: { ...v.appearance, avatar: reader.result as string },
                            }
                          : v,
                      );
                    else setError('The avatar was not applied because editing is locked.');
                  };
                  reader.onerror = () => {
                    if (finish()) setError('The image could not be read.');
                  };
                  try {
                    reader.readAsDataURL(file);
                  } catch {
                    if (finish()) setError('The image could not be read.');
                  }
                }}
              />
            </Button>
            <Button
              disabled={locked || (!draft.appearance.avatar && !uploading)}
              onClick={() => {
                cancelUpload();
                appearance({ avatar: null });
              }}>
              Remove avatar
            </Button>
          </Stack>
          {uploading && <Typography role="status">Reading avatar…</Typography>}
          {(['title', 'cta', 'start', 'end', 'placeholder'] as const).map((key) => (
            <TextField
              key={key}
              label={
                {
                  title: 'Widget name',
                  cta: 'Call to action',
                  start: 'Start button',
                  end: 'End button',
                  placeholder: 'Chat placeholder',
                }[key]
              }
              value={draft.appearance[key]}
              disabled={locked}
              onChange={(e) => appearance({ [key]: e.target.value })}
            />
          ))}
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.appearance.termsEnabled}
                disabled={locked}
                onChange={(e) => appearance({ termsEnabled: e.target.checked })}
              />
            }
            label="Require acceptance of terms"
          />
          {draft.appearance.termsEnabled && (
            <TextField
              label="Terms HTTPS URL"
              value={draft.appearance.termsUrl ?? ''}
              disabled={locked}
              onChange={(e) => appearance({ termsUrl: e.target.value || null })}
            />
          )}
          {invalid && <Alert severity="warning">{invalid}</Alert>}
        </Stack>
        <Stack gap={2}>
          <Typography variant="h6">Appearance preview</Typography>
          <WidgetAppearancePreview value={draft.appearance} />
          <Typography variant="caption">
            Appearance only. This preview cannot create a session or send messages.
          </Typography>
          {baseline && (
            <>
              <Typography variant="h6">Website embed</Typography>
              <Typography variant="body2">
                Add this script on an allowed website after publishing. Start requests an anonymous
                session only after an explicit visitor gesture. HTTPS and microphone permission are
                required for voice.
              </Typography>
              <TextField
                multiline
                label="Embed code"
                value={widgetEmbedCode(baseline.id)}
                slotProps={{ input: { readOnly: true } }}
              />
              <Button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(widgetEmbedCode(baseline.id));
                    if (alive.current) setNotice('Embed code copied.');
                  } catch {
                    if (alive.current)
                      setNotice('Clipboard unavailable. Select and copy the embed code above.');
                  }
                }}>
                Copy embed code
              </Button>
            </>
          )}
        </Stack>
      </Box>
      <Dialog
        open={!!review}
        onClose={() => {
          if (!busy.current) setReview(null);
        }}
        fullWidth
        maxWidth="sm">
        <DialogTitle>
          {review === 'publish' ? 'Publish Widget configuration?' : 'Disable new visitor sessions?'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {review === 'publish'
              ? 'The saved configuration below becomes public. Allowed origins and explicit spending caps are enforced by the server. Existing sessions retain their previous captured settings.'
              : 'New sessions stop immediately. Active conversations may finish under their captured limits.'}
          </Typography>
          {review === 'publish' && baseline && (
            <Box
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', overflow: 'auto', overflowWrap: 'anywhere' }}>
              {JSON.stringify(
                {
                  ...baseline.configuration,
                  appearance: {
                    ...baseline.configuration.appearance,
                    avatar: baseline.configuration.appearance.avatar?.startsWith('data:')
                      ? 'Uploaded raster image'
                      : baseline.configuration.appearance.avatar,
                  },
                },
                null,
                2,
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving || publishing || disabling} onClick={() => setReview(null)}>
            Cancel
          </Button>
          <Button disabled={locked || dirty} onClick={() => review && void perform(review)}>
            Confirm {review}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
export const WidgetAppearancePreview = ({ value }: { value: WidgetAppearance }) => {
  const theme = useMemo(() => createWidgetTheme(value.colors), [value.colors]);
  return (
    <ThemeProvider theme={theme}>
      <Paper
        variant="outlined"
        sx={{ bgcolor: 'background.paper', color: 'text.primary', overflow: 'hidden' }}>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <Avatar src={value.avatar ?? undefined} imgProps={{ referrerPolicy: 'no-referrer' }}>
            {value.title.slice(0, 1)}
          </Avatar>
          <Typography>{value.size === 'tiny' ? value.cta : value.title}</Typography>
        </Stack>
        {value.size !== 'tiny' && (
          <Stack gap={2} sx={{ p: 2 }}>
            <Typography>{value.cta}</Typography>
            {value.mode === 'chat' ? (
              <TextField label={value.placeholder} disabled />
            ) : (
              <Button disabled>{value.start}</Button>
            )}
            {value.termsEnabled && <Typography variant="caption">Terms apply</Typography>}
          </Stack>
        )}
      </Paper>
    </ThemeProvider>
  );
};
